import { Controller } from "@hotwired/stimulus"
import { useClickOutside } from "stimulus-use"
import { debounce, template, escape } from "lodash"

export default class extends Controller {
  static targets = ["checkbox", "wrapper", "nameInput", "contractorId", "destroy", "menu", "list", "itemTemplate", "preview"]
  static values = { url: String, roleLabel: String, role: String }

  searchAbortController = null

  connect() {
    useClickOutside(this)
    this.fetchResults = debounce(this.fetchResults, 300).bind(this)
    this.renderItem = template(this.itemTemplateTarget.innerHTML)
  }

  disconnect() {
    this.searchAbortController?.abort()
  }

  toggle() {
    const checked = this.checkboxTarget.checked
    this.wrapperTarget.classList.toggle("hidden", !checked)
    if (checked) {
      // bez wybranego centrum rekord ma zostac usuniety (destroy=1) — inaczej persisted wiersz zapisalby sie z pustym contractor_id i stara nazwa/dir3
      this.destroyTarget.value = this.contractorIdTarget.value ? "0" : "1"
    } else {
      this.destroyTarget.value = "1"
      this.nameInputTarget.value = ""
      this.contractorIdTarget.value = ""
      this.previewTarget.textContent = ""
    }
  }

  search() {
    // edycja tekstu unieważnia poprzedni wybór z podpowiedzi — natychmiast (bez debounce),
    // inaczej zostawiony contractor_id nadpisałby przy zapisie widoczną nazwę danymi
    // starego centrum (copy_from_contractor), a XML B2G poszedłby z cudzym DIR3
    this.contractorIdTarget.value = ""
    this.destroyTarget.value = "1"
    this.fetchResults()
  }

  fetchResults() {
    const term = this.nameInputTarget.value.trim()
    if (term.length < 1) {
      this.hideMenu()
      return
    }

    // bez tego wolniejsza starsza odpowiedź nadpisałaby nowszą, a odpowiedź po clear() znów otworzyłaby menu
    this.searchAbortController?.abort()
    this.searchAbortController = new AbortController()

    const csrfToken = document.querySelector("meta[name='csrf-token']").content
    fetch(this.urlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-CSRF-Token": csrfToken,
        Accept: "application/json"
      },
      body: new URLSearchParams({ name: term, rows: 20, role: this.roleValue }),
      signal: this.searchAbortController.signal
    })
      .then(r => r.json())
      .then(data => this.renderResults(data))
      .catch(e => {
        if (e.name === "AbortError") return

        this.hideMenu()
      })
  }

  renderResults(data) {
    if (!data.length) {
      this.hideMenu()
      return
    }

    // escape — wartości trafiają do atrybutów/treści HTML w szablonie lodash (${...} nie escapuje)
    this.listTarget.innerHTML = data.map(center => this.renderItem({
      id: center.id,
      label: escape(center.label),
      name: escape(center.name || ""),
      dir3_code: escape(center.dir3_code || ""),
      street: escape(center.street || ""),
      post_code: escape(center.post_code || ""),
      city: escape(center.city || "")
    })).join("")
    this.menuTarget.classList.remove("hidden")
  }

  select({ params }) {
    const { id, name, dir3Code, street, postCode, city } = params
    const address = [street, [postCode, city].filter(Boolean).join(" ")].filter(Boolean).join(", ")

    this.contractorIdTarget.value = id
    this.nameInputTarget.value = name
    this.destroyTarget.value = "0"
    this.previewTarget.textContent = [this.roleLabelValue, name, dir3Code, address].filter(Boolean).join(", ")

    this.hideMenu()
  }

  clear() {
    this.nameInputTarget.value = ""
    this.contractorIdTarget.value = ""
    // bez wybranego centrum rekord ma zostać usunięty przy zapisie — ta sama zasada co w toggle()
    this.destroyTarget.value = "1"
    this.previewTarget.textContent = ""
    this.hideMenu()
    this.nameInputTarget.focus()
  }

  hideMenu() {
    this.searchAbortController?.abort()
    this.menuTarget.classList.add("hidden")
    this.listTarget.innerHTML = ""
  }

  clickOutside() {
    this.hideMenu()
  }
}
